import { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { useDispatch } from "react-redux";
import InvoiceSelect from "components/FormControls/ThemedSelect";
import { recordExpensePayment } from "utils/api";
import { formatCurrencyAUD } from "utils/formatCurrency";

const localDate = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const cleanAmount = (value) => {
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  const [whole = "", ...fraction] = cleaned.split(".");
  return `${whole.slice(0, 10)}${
    cleaned.includes(".") ? `.${fraction.join("").slice(0, 2)}` : ""
  }`;
};

const toDateKey = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const formatPaymentDate = (value, short = false) => {
  const date = new Date(`${toDateKey(value)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: short ? "short" : "long",
    year: "numeric",
  });
};

const samePayment = (payment, form) =>
  Number(payment?.amount).toFixed(2) === Number(form.amount).toFixed(2) &&
  toDateKey(payment?.paymentDate) === toDateKey(form.paymentDate) &&
  String(payment?.paymentMethod || "").trim().toLowerCase() ===
    String(form.paymentMethod || "").trim().toLowerCase() &&
  String(payment?.reference || "").trim().toLowerCase() ===
    String(form.reference || "").trim().toLowerCase();

function RecordExpensePaymentDialog({ expense, onClose, onRecorded }) {
  const dispatch = useDispatch();
  const amountRef = useRef(null);
  const outstanding = Number(expense?.outstandingAmount) || 0;
  const expenseTotal = Number(expense?.totalAmount) || outstanding;
  const [form, setForm] = useState({
    amount: outstanding ? outstanding.toFixed(2) : "",
    paymentDate: localDate(),
    paymentMethod: "Bank transfer",
    reference: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [requestError, setRequestError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [review, setReview] = useState("");
  const [result, setResult] = useState(null);

  const amount = Number(form.amount);
  const projected = useMemo(
    () =>
      form.amount && Number.isFinite(amount) && amount > 0 && amount <= outstanding
        ? Math.max(0, outstanding - amount)
        : null,
    [amount, form.amount, outstanding]
  );
  const existingDuplicate = useMemo(
    () => (expense?.payments || []).find((payment) => samePayment(payment, form)),
    [expense?.payments, form]
  );

  const supplier = expense.companyName || expense.name || "Supplier";
  const expenseReference = expense.referenceNumber || expense._id;

  useEffect(() => {
    if (!review && !result) amountRef.current?.focus();
  }, [result, review]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [onClose, submitting]);

  const update = (field) => (event) => {
    const value =
      field === "amount" ? cleanAmount(event.target.value) : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: "" }));
    setRequestError("");
    setReview("");
  };

  const validate = () => {
    const errors = {};
    if (!form.amount || !Number.isFinite(amount) || amount <= 0 || amount > outstanding) {
      errors.amount = `Amount must be greater than $0.00 and no more than outstanding $${formatCurrencyAUD(
        outstanding
      )}.`;
    }
    if (!form.paymentDate || !toDateKey(form.paymentDate) || form.paymentDate > localDate()) {
      errors.paymentDate = "Enter a valid payment date.";
    }
    if (!form.paymentMethod) errors.paymentMethod = "Choose a payment method";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const sendPayment = async ({ confirmDuplicate = false } = {}) => {
    setReview("");
    setRequestError("");
    setSubmitting(true);
    await recordExpensePayment(
      { _id: expense._id, ...form, amount, confirmDuplicate },
      (data) => {
        if (data.Result_Status) {
          setResult(data.Result_Output);
          onRecorded(data.Result_Output);
        } else if (
          String(data.Result_Message || "")
            .toLowerCase()
            .includes("already be recorded")
        ) {
          setReview("duplicate");
        } else {
          setRequestError(data.Result_Message || "Payment could not be recorded.");
        }
      },
      dispatch
    );
    setSubmitting(false);
  };

  const submit = (event) => {
    event?.preventDefault();
    if (!validate()) return;
    if (existingDuplicate) {
      setReview("duplicate");
      return;
    }
    if (projected === 0) {
      setReview("remaining");
      return;
    }
    sendPayment();
  };

  const resetForRemaining = () => {
    const nextOutstanding = Number(result?.outstandingAmount) || 0;
    setForm({
      amount: nextOutstanding.toFixed(2),
      paymentDate: localDate(),
      paymentMethod: "Bank transfer",
      reference: "",
    });
    setFieldErrors({});
    setRequestError("");
    setReview("");
    setResult(null);
  };

  const currentPayments = expense.payments || [];
  const resultAlreadyPresent = result?.payment
    ? currentPayments.some(
        (payment) =>
          (payment._id && result.payment._id && payment._id === result.payment._id) ||
          samePayment(payment, result.payment)
      )
    : false;
  const paymentHistory = result?.payment && !resultAlreadyPresent
    ? [...currentPayments, result.payment]
    : currentPayments;

  return (
    <div
      className="expense-payment-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <section
        className="expense-payment-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="expense-payment-title"
      >
        <div className="expense-payment-grab" aria-hidden="true" />
        {submitting ? (
          <div className="expense-payment-pending" aria-live="polite">
            <h2 id="expense-payment-title">Recording payment</h2>
            <p>
              {expenseReference} · {supplier} · Outstanding ${formatCurrencyAUD(
                outstanding
              )} AUD
            </p>
            <label>
              <span>Payment amount · AUD</span>
              <input value={`$${formatCurrencyAUD(amount)}`} disabled readOnly />
            </label>
            <p>
              {formatPaymentDate(form.paymentDate)} · {form.paymentMethod}
              {form.reference ? ` · ${form.reference}` : ""}
            </p>
            <div className="expense-payment-balance is-projected">
              <span>Projected remaining</span>
              <strong>${formatCurrencyAUD(projected || 0)}</strong>
            </div>
            <button className="expense-primary" type="button" disabled aria-busy="true">
              <span className="expense-button-spinner" aria-hidden="true" />
              Recording payment…
            </button>
            <p className="expense-payment-help">
              Records a payment made elsewhere. No money is transferred. Values
              retained; balance changes only on confirmed success.
            </p>
            <span className="expense-payment-status">Recording</span>
            <p className="expense-payment-footnote">
              Duplicate submissions disabled. No updated balance or success state
              until confirmed. No second expense posted.
            </p>
          </div>
        ) : result ? (
          <div className="expense-payment-result" aria-live="polite">
            <div className="expense-payment-success-title">
              <FiCheckCircle aria-hidden="true" />
              <div>
                <h2 id="expense-payment-title">
                  {result.paymentStatus === "Paid" ? "Paid in full" : "Payment recorded"}
                </h2>
                <p>${formatCurrencyAUD(result.payment?.amount)} payment recorded once</p>
              </div>
            </div>
            <div className="expense-payment-result-badges">
              <span>Saved / Posted</span>
              <span>{result.paymentStatus}</span>
              <span className="warning">
                {expense.uploadDocument ? "Receipt attached" : "Receipt missing"}
              </span>
            </div>
            <dl>
              <div>
                <dt>Expense total · unchanged</dt>
                <dd>${formatCurrencyAUD(expenseTotal)}</dd>
              </div>
              <div>
                <dt>Payments recorded</dt>
                <dd>${formatCurrencyAUD(result.paidAmount)}</dd>
              </div>
              <div>
                <dt>Remaining balance</dt>
                <dd>${formatCurrencyAUD(result.outstandingAmount)}</dd>
              </div>
            </dl>
            <div className="expense-payment-result-history">
              <strong>
                Payment history · {paymentHistory.length} payment
                {paymentHistory.length === 1 ? "" : "s"}
              </strong>
              {paymentHistory.map((payment, index) => (
                <span
                  key={
                    payment._id ||
                    payment.createdAt ||
                    `${payment.paymentDate}-${index}`
                  }
                >
                  {formatPaymentDate(payment.paymentDate, true)} · {payment.paymentMethod} · ${
                    formatCurrencyAUD(payment.amount)
                  }
                  {payment.reference ? ` · ${payment.reference}` : ""}
                </span>
              ))}
            </div>
            {result.paymentStatus === "Paid" && (
              <div className="expense-payment-guidance">
                <strong>Paid in full</strong>
                <span>
                  Each payment is recorded once. No Record payment or Pay now
                  action is offered. Missing receipt remains separate from payment.
                </span>
              </div>
            )}
            <div className="expense-payment-actions">
              {Number(result.outstandingAmount) > 0 ? (
                <button className="expense-primary" type="button" onClick={resetForRemaining}>
                  Record remaining payment
                </button>
              ) : (
                <button className="expense-primary" type="button" onClick={onClose}>
                  View expense
                </button>
              )}
            </div>
          </div>
        ) : review ? (
          <div className="expense-payment-review">
            <h2 id="expense-payment-title">
              {review === "duplicate"
                ? "This payment may already be recorded"
                : "Record the remaining payment?"}
            </h2>
            <p>
              {review === "duplicate"
                ? `${expenseReference} has ${
                    form.reference || "a matching payment"
                  } · ${formatPaymentDate(form.paymentDate, true)} · $${formatCurrencyAUD(
                    amount
                  )}. Review the existing history before recording another payment. Remaining balance is $${formatCurrencyAUD(
                    outstanding
                  )}.`
                : `Record $${formatCurrencyAUD(amount)} paid elsewhere on ${formatPaymentDate(
                    form.paymentDate,
                    true
                  )}. Remaining balance will be $0.00 after confirmed success. The original purchase is not posted again.`}
            </p>
            <div className="expense-payment-actions">
              <button
                className="expense-detail-secondary"
                type="button"
                onClick={() => setReview("")}
              >
                Cancel
              </button>
              <button
                className="expense-primary"
                type="button"
                onClick={() => sendPayment({ confirmDuplicate: review === "duplicate" })}
              >
                Confirm payment
              </button>
            </div>
            <div className="expense-payment-balance is-projected">
              <span>Current remaining balance</span>
              <strong>${formatCurrencyAUD(outstanding)}</strong>
            </div>
            {review === "duplicate" && (
              <p className="expense-payment-footnote">
                A duplicate reference warning does not itself confirm a new payment.
                Keep the existing history unchanged until you confirm.
              </p>
            )}
          </div>
        ) : (
          <form className="expense-payment-form" onSubmit={submit} noValidate>
            <h2 id="expense-payment-title">Record payment</h2>
            <p className="expense-payment-supplier">
              {expenseReference} · {supplier}
            </p>
            <div className="expense-payment-balance">
              <span>Outstanding before payment</span>
              <strong>${formatCurrencyAUD(outstanding)}</strong>
            </div>
            {requestError && (
              <div className="expense-payment-error-banner" role="alert">
                <FiAlertCircle aria-hidden="true" />
                <div>
                  <strong>Payment wasn&apos;t recorded</strong>
                  <span>{requestError}</span>
                  <small>
                    The amount, date, method and reference are retained. Remaining
                    balance is still ${formatCurrencyAUD(outstanding)} until a
                    confirmed result.
                  </small>
                </div>
              </div>
            )}
            <label className={fieldErrors.amount ? "has-error" : ""}>
              <span>Payment amount · AUD</span>
              <input
                ref={amountRef}
                inputMode="decimal"
                value={form.amount}
                onChange={update("amount")}
                aria-invalid={Boolean(fieldErrors.amount) || undefined}
                aria-describedby={fieldErrors.amount ? "payment-amount-error" : undefined}
              />
              {fieldErrors.amount && (
                <small id="payment-amount-error" role="alert">
                  {fieldErrors.amount}
                </small>
              )}
            </label>
            <label className={fieldErrors.paymentDate ? "has-error" : ""}>
              <span>Payment date</span>
              <input
                type="date"
                max={localDate()}
                value={form.paymentDate}
                onChange={update("paymentDate")}
                aria-invalid={Boolean(fieldErrors.paymentDate) || undefined}
                aria-describedby={fieldErrors.paymentDate ? "payment-date-error" : undefined}
              />
              {fieldErrors.paymentDate && (
                <small id="payment-date-error" role="alert">
                  {fieldErrors.paymentDate}
                </small>
              )}
            </label>
            <label
              className={`expense-payment-method-field${
                fieldErrors.paymentMethod ? " has-error" : ""
              }`}
            >
              <span>Payment method</span>
              <InvoiceSelect
                aria-label="Payment method"
                aria-invalid={Boolean(fieldErrors.paymentMethod) || undefined}
                value={form.paymentMethod}
                onChange={update("paymentMethod")}
                displayValue={form.paymentMethod || "Choose a configured method"}
                menuBoundarySelector=".expense-payment-dialog"
              >
                <option value="">Choose a configured method</option>
                <option value="Bank transfer">Bank transfer</option>
                <option value="Card">Card</option>
                <option value="Cash">Cash</option>
                <option value="Direct debit">Direct debit</option>
                <option value="Other">Other</option>
              </InvoiceSelect>
            </label>
            {fieldErrors.paymentMethod && (
              <div className="expense-payment-method-error" role="alert">
                <FiAlertCircle aria-hidden="true" />
                <div>
                  <strong>Choose a payment method</strong>
                  <span>
                    Select a supported outgoing method. An illustrative method name
                    is not proof of configured integration.
                  </span>
                </div>
              </div>
            )}
            <label>
              <span>Payment reference</span>
              <input
                maxLength={80}
                value={form.reference}
                onChange={update("reference")}
                placeholder="e.g. DELTA-PMT-01"
              />
            </label>
            <div className="expense-payment-balance is-projected">
              <span>Projected remaining balance</span>
              <strong>
                {projected === null ? "—" : `$${formatCurrencyAUD(projected)}`}
              </strong>
            </div>
            <p className="expense-payment-help">
              Record payment records a payment made elsewhere. It does not transfer
              money.
            </p>
            <div className="expense-payment-actions">
              <button className="expense-detail-secondary" type="button" onClick={onClose}>
                Cancel
              </button>
              <button
                className="expense-primary"
                type="submit"
                disabled={Object.values(fieldErrors).some(Boolean)}
              >
                Record payment
              </button>
            </div>
            <button
              className="expense-payment-mobile-close"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
            <p className="expense-payment-footnote">
              Records payment once and reduces the payable. Recorded spend remains ${
                formatCurrencyAUD(expenseTotal)
              }. No supplier email or bank transfer is executed.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}

export default RecordExpensePaymentDialog;
